import React, { useMemo } from "react";
import LeagueTable from "../components/LeagueSummary_Table";
import footballHero from "../assets/nashville_trp.JPG";

const ownerDisplayNameOverrides = {
  "Bat Man": "Kyle Seys",
  "Dennis Mcmeekan": "Dennis McMeekan",
};

function normalizeOwnerDisplayName(name) {
  return ownerDisplayNameOverrides[name] || name;
}

function normalizeSummaryRow(row) {
  return {
    ...row,
    name: normalizeOwnerDisplayName(row.name),
  };
}

function normalizeRawRow(row) {
  return {
    ...row,
    owner_name: normalizeOwnerDisplayName(row.owner_name),
  };
}

// Kick off the hero image fetch as early as possible so it's ready by the time it renders.
if (typeof document !== "undefined" && !document.querySelector(`link[href="${footballHero}"]`)) {
  const heroPreload = document.createElement("link");
  heroPreload.rel = "preload";
  heroPreload.as = "image";
  heroPreload.href = footballHero;
  heroPreload.fetchPriority = "high";
  document.head.appendChild(heroPreload);
}

function StatCard({ label, value, tone = "default" }) {
  const tones = {
    default: "var(--card-bg-default)",
    accent: "var(--card-bg-accent)",
    cool: "var(--card-bg-cool)",
  };

  return (
    <div
      className="adaptive-panel border shadow-lg shadow-black/10"
      style={{ borderColor: "var(--border-soft)", background: tones[tone] }}
    >
      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[var(--muted-text)]">{label}</p>
      <p className="mt-3 text-3xl font-semibold text-[var(--text-main)]">{value}</p>
    </div>
  );
}

function DetailStatCard({ label, value, detail, tone = "default" }) {
  const tones = {
    default: "var(--card-bg-default)",
    accent: "var(--card-bg-accent)",
    cool: "var(--card-bg-cool)",
  };

  return (
    <div
      className="adaptive-panel border shadow-lg shadow-black/10"
      style={{ borderColor: "var(--border-soft)", background: tones[tone] }}
    >
      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[var(--muted-text)]">{label}</p>
      <p className="mt-3 text-3xl font-semibold text-[var(--text-main)]">{value}</p>
      {detail ? <p className="mt-2 text-sm text-[var(--muted-text)]">{detail}</p> : null}
    </div>
  );
}

function SectionHeading({ eyebrow, title }) {
  return (
    <div>
      {eyebrow ? <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[var(--muted-text)]">{eyebrow}</p> : null}
      <h2 className={`theme-display text-2xl font-medium text-[var(--text-main)] ${eyebrow ? "mt-2" : ""}`}>{title}</h2>
    </div>
  );
}

function TableShell({ children }) {
  return (
    <div className="mx-auto w-full min-w-0 max-w-full">
      <div
        className="adaptive-table-shell w-full overflow-hidden border shadow-2xl shadow-black/10"
        style={{ borderColor: "var(--border-soft)", background: "var(--table-shell-bg)" }}
      >
        <div className="w-full max-w-full overflow-x-auto overscroll-x-contain ios-scroll">{children}</div>
      </div>
    </div>
  );
}

function LedgerStrip({ label, value, detail }) {
  return (
    <div className="border-b px-4 py-4 sm:px-5" style={{ borderColor: "var(--border-soft)" }}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[var(--accent-soft)]">{label}</p>
          {detail ? <p className="mt-2 text-sm text-[var(--muted-text)]">{detail}</p> : null}
        </div>
        <p className="theme-display text-2xl font-semibold text-[var(--text-main)] sm:text-3xl">{value}</p>
      </div>
    </div>
  );
}

function Plaque({ title, value, detail }) {
  return (
    <div
      className="h-full rounded-[1.5rem] border p-2 shadow-2xl shadow-black/20"
      style={{
        borderColor: "rgba(109, 68, 45, 0.9)",
        background: "linear-gradient(135deg, #6b3924 0%, #7a3e28 45%, #5d2f1f 100%)",
        boxShadow: "inset 0 0 0 1px rgba(255, 232, 192, 0.08)",
      }}
    >
      <div
        className="relative flex h-full min-h-[152px] flex-col items-center justify-center overflow-hidden rounded-[1.15rem] border px-5 py-6 text-center"
        style={{
          borderColor: "transparent",
          background: "linear-gradient(180deg, #102026 0%, #09161b 100%)",
          clipPath: "polygon(5% 0, 95% 0, 100% 10%, 100% 90%, 95% 100%, 5% 100%, 0 90%, 0 10%)",
          boxShadow: "inset 0 0 0 1px rgba(244, 219, 150, 0.08)",
        }}
      >
        <div
          className="pointer-events-none absolute inset-0"
        >
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full">
            <path
              d="M5 0 H95 L100 10 V90 L95 100 H5 L0 90 V10 Z"
              fill="none"
              stroke="rgba(230, 198, 122, 0.92)"
              strokeWidth="2.2"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>
        <div className="pointer-events-none absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at top, rgba(255,255,255,0.14), transparent 34%)" }} />
        <div className="pointer-events-none absolute left-4 top-4 h-2 w-2 rounded-full" style={{ background: "rgba(241, 212, 136, 0.98)", boxShadow: "0 0 0 1px rgba(120, 79, 30, 0.55)" }} />
        <div className="pointer-events-none absolute right-4 top-4 h-2 w-2 rounded-full" style={{ background: "rgba(241, 212, 136, 0.98)", boxShadow: "0 0 0 1px rgba(120, 79, 30, 0.55)" }} />
        <p className="relative mb-4 text-[10px] font-semibold uppercase tracking-[0.32em] text-[var(--accent-soft)] sm:text-[11px]">{title}</p>
        <p className="theme-display relative mt-2 text-3xl font-medium text-[var(--text-main)] sm:text-4xl">{value}</p>
        {detail ? <p className="relative mt-3 text-sm text-[var(--muted-text)]">{detail}</p> : null}
      </div>
    </div>
  );
}

function ClassicPlaque({ title, value, detail }) {
  return (
    <div
      className="h-full rounded-[1.75rem] border p-1 shadow-2xl shadow-black/15"
      style={{ borderColor: "var(--border-strong)", background: "linear-gradient(135deg, rgba(255,255,255,0.02), rgba(0,0,0,0.12))" }}
    >
      <div
        className="flex h-full min-h-[152px] flex-col items-center justify-center rounded-[1.45rem] border px-5 py-6 text-center"
        style={{ borderColor: "var(--border-soft)", background: "var(--panel-strong-bg)" }}
      >
        <div className="mb-4 flex justify-center">
          <span
            className="inline-flex rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.28em] sm:text-[11px]"
            style={{
              borderColor: "rgba(242, 223, 180, 0.22)",
              background: "rgba(212, 173, 91, 0.08)",
              color: "var(--accent-soft)",
            }}
          >
            {title}
          </span>
        </div>
        <p className="theme-display mt-4 text-3xl font-medium text-[var(--text-main)] sm:text-4xl">{value}</p>
        {detail ? <p className="mt-3 text-sm text-[var(--muted-text)]">{detail}</p> : null}
      </div>
    </div>
  );
}

function TrophyGalleryFrame({ children, subtle = false }) {
  return (
    <div
      className="adaptive-table-shell relative overflow-hidden border shadow-2xl shadow-black/15"
      style={{
        borderColor: "var(--border-strong)",
        background: subtle ? "rgba(66, 42, 31, 0.4)" : "rgba(66, 42, 31, 0.58)",
      }}
    >
      <div className="relative">{children}</div>
    </div>
  );
}

function TrophyHeroTitle() {
  return (
    <div className="relative z-10 mx-auto w-full max-w-3xl text-center">
      <div className="mb-5 flex items-center justify-center gap-4 sm:mb-6 sm:gap-6">
        <span
          className="h-px flex-1 max-w-24 sm:max-w-40"
          style={{ background: "linear-gradient(90deg, transparent, rgba(242, 223, 180, 0.7))" }}
        />
        <span
          className="inline-flex h-3 w-3 rounded-full border"
          style={{
            borderColor: "rgba(242, 223, 180, 0.6)",
            background: "radial-gradient(circle, rgba(255, 245, 225, 0.95) 0%, rgba(212, 173, 91, 0.85) 55%, rgba(212, 173, 91, 0.1) 100%)",
            boxShadow: "0 0 18px rgba(212, 173, 91, 0.28)",
          }}
        />
        <span
          className="h-px flex-1 max-w-24 sm:max-w-40"
          style={{ background: "linear-gradient(90deg, rgba(242, 223, 180, 0.7), transparent)" }}
        />
      </div>

      <h1 className="theme-display text-center text-3xl font-medium tracking-tight text-[var(--text-main)] sm:text-5xl">
        Sunday Ledger
      </h1>
      <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.34em] text-[var(--accent-soft)] sm:text-xs">
        Elite Fantasy Football
      </p>
    </div>
  );
}

function ClassyOverview({ theme, activeOwners, formerOwners, activeChampions, expelledMembers, mostPostseasonAppearances, totalSeasons, bestRecord, bestActiveWinRate }) {
  return (
    <div className="adaptive-stack">
      <section className="grid gap-6 lg:grid-cols-[1.5fr,1fr]">
        <div
          className="adaptive-panel border shadow-2xl shadow-black/20"
          style={{ borderColor: "var(--border-soft)", background: "var(--hero-bg)" }}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[var(--accent-soft)]">
            {theme.heroEyebrow}
          </p>
          <h1 className="theme-display mt-4 max-w-2xl text-3xl font-semibold tracking-tight text-[var(--text-main)] sm:text-5xl">
            {theme.heroTitle}
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-[var(--muted-text)] sm:text-base">
            {theme.heroBody}
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
          <StatCard label="Total Active Champions" value={activeChampions || "0"} tone="accent" />
          <StatCard label="Banished Members" value={expelledMembers || "0"} tone="cool" />
          <DetailStatCard
            label="Most Postseason Appearances"
            value={mostPostseasonAppearances.value}
            detail={mostPostseasonAppearances.name}
          />
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Total Seasons" value={totalSeasons || "--"} />
        <DetailStatCard label="Best Season Record" value={bestRecord.value} detail={bestRecord.name} />
        <DetailStatCard
          label="Best Active Win Rate"
          value={bestActiveWinRate.value}
          detail={bestActiveWinRate.name}
          tone="cool"
        />
      </section>

      <section>
        <div className="mb-4 flex items-end justify-between gap-4">
          <SectionHeading eyebrow="Current Field" title="Active Managers" />
        </div>
        <TableShell>
          <LeagueTable stats={activeOwners} highlight={true} />
        </TableShell>
      </section>

      {formerOwners.length > 0 && (
        <section>
          <div className="mb-4 flex items-end justify-between gap-4">
            <SectionHeading eyebrow="Past Eras" title="Former Managers" />
          </div>
          <TableShell>
            <LeagueTable stats={formerOwners} highlight={false} />
          </TableShell>
        </section>
      )}
    </div>
  );
}

function SportsbookOverview({ theme, activeOwners, formerOwners, activeChampions, expelledMembers, mostPostseasonAppearances, totalSeasons, bestRecord, bestActiveWinRate }) {
  return (
    <div className="adaptive-stack">
      <section
        className="overflow-hidden rounded-[1rem] border shadow-2xl shadow-black/25"
        style={{ borderColor: "var(--border-strong)", background: "var(--hero-bg)" }}
      >
        <div className="grid gap-0 lg:grid-cols-[1.4fr,1fr]">
          <div className="adaptive-panel border-b lg:border-b-0 lg:border-r" style={{ borderColor: "var(--border-soft)" }}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.42em] text-[var(--accent-soft)]">
              {theme.heroEyebrow}
            </p>
            <h1 className="theme-display mt-4 max-w-2xl text-3xl font-semibold tracking-tight text-[var(--text-main)] sm:text-5xl">
              {theme.heroTitle}
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-[var(--muted-text)] sm:text-base">
              {theme.heroBody}
            </p>
          </div>
          <div className="divide-y" style={{ borderColor: "var(--border-soft)", background: "var(--panel-strong-bg)" }}>
            <LedgerStrip label="House Notes" value={totalSeasons || "--"} detail="Total seasons on record" />
            <LedgerStrip label="Best Ticket" value={bestRecord.value} detail={bestRecord.name} />
            <LedgerStrip label="Sharpest Active Manager" value={bestActiveWinRate.value} detail={bestActiveWinRate.name} />
          </div>
        </div>
      </section>

      <section
        className="overflow-hidden rounded-[1rem] border shadow-xl shadow-black/20"
        style={{ borderColor: "var(--border-soft)", background: "var(--table-shell-bg)" }}
      >
        <div
          className="border-b px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.36em] text-[var(--accent-soft)] sm:px-6"
          style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}
        >
          Sunday Board
        </div>
        <div className="grid gap-0 md:grid-cols-4">
          <LedgerStrip label="Active Champions" value={activeChampions || "0"} />
          <LedgerStrip label="Banished Members" value={expelledMembers || "0"} />
          <LedgerStrip label="Most Postseason Appearances" value={mostPostseasonAppearances.value} detail={mostPostseasonAppearances.name} />
          <LedgerStrip label="Current Theme" value="SPORTSBOOK" detail="Printed lines, ledger strips, and house-board rhythm." />
        </div>
      </section>

      <section>
        <div className="mb-4 flex items-end justify-between gap-4">
          <SectionHeading eyebrow="Board Sheet" title="Active Managers" />
        </div>
        <TableShell>
          <LeagueTable stats={activeOwners} highlight={true} />
        </TableShell>
      </section>

      {formerOwners.length > 0 && (
        <section>
          <div className="mb-4 flex items-end justify-between gap-4">
            <SectionHeading eyebrow="Archived Lines" title="Former Managers" />
          </div>
          <TableShell>
            <LeagueTable stats={formerOwners} highlight={false} />
          </TableShell>
        </section>
      )}
    </div>
  );
}

function TrophyOverview({ theme, activeOwners, formerOwners, activeChampions, expelledMembers, mostPostseasonAppearances, totalSeasons, bestRecord, bestActiveWinRate }) {
  return (
    <div className="adaptive-stack">
      <section>
        <TrophyGalleryFrame>
          <div className="grid gap-6 xl:grid-cols-[1.2fr,0.8fr]">
            <div
              className="relative h-full rounded-[2.05rem] border p-2 shadow-2xl shadow-black/20"
              style={{ borderColor: "var(--border-strong)", background: "linear-gradient(135deg, rgba(255,255,255,0.02), rgba(0,0,0,0.12))" }}
            >
              <div
                className="relative flex h-full min-h-[320px] items-start justify-center overflow-hidden rounded-[1.75rem] border px-6 py-8 sm:px-8"
                style={{ borderColor: "var(--border-soft)", background: "#050505" }}
              >
                <div className="pointer-events-none absolute inset-0">
                  <img
                    src={footballHero}
                    alt=""
                    className="h-[calc(100%+0.5rem)] w-full translate-y-28 object-cover object-[center_top] sm:translate-y-[5.5rem]"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                </div>
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{ background: "linear-gradient(180deg, rgba(6, 6, 6, 0.94) 0%, rgba(6, 6, 6, 0.8) 22%, rgba(6, 6, 6, 0.35) 42%, rgba(6, 6, 6, 0.06) 60%, rgba(6, 6, 6, 0) 72%)" }}
                />
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{ boxShadow: "inset 0 0 0 1px rgba(242, 223, 180, 0.08)" }}
                />
                <div className="absolute inset-x-6 top-8 flex justify-center sm:inset-x-8 sm:top-10">
                  <TrophyHeroTitle />
                </div>
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-1">
              <Plaque title="Total Active Champions" value={activeChampions || "0"} />
              <Plaque title="Most Postseason Appearances" value={mostPostseasonAppearances.value} detail={mostPostseasonAppearances.name} />
            </div>
          </div>
        </TrophyGalleryFrame>
      </section>

      <section>
        <TrophyGalleryFrame subtle={true}>
          <div className="grid gap-5 md:grid-cols-3">
            <Plaque title="Total Seasons" value={totalSeasons || "--"} />
            <Plaque title="Best Season Record" value={bestRecord.value} detail={bestRecord.name} />
            <Plaque title="Best Active Win Rate" value={bestActiveWinRate.value} detail={bestActiveWinRate.name} />
          </div>
        </TrophyGalleryFrame>
      </section>

      <section
        className="adaptive-panel border shadow-2xl shadow-black/15"
        style={{ borderColor: "var(--border-soft)", background: "var(--panel-strong-bg)" }}
      >
        <div className="mb-5 flex items-end justify-between gap-4">
          <SectionHeading eyebrow="" title="Active Managers" />
        </div>
        <TableShell>
          <LeagueTable stats={activeOwners} highlight={true} />
        </TableShell>
      </section>

      {formerOwners.length > 0 && (
        <section
          className="adaptive-panel border shadow-2xl shadow-black/15"
          style={{ borderColor: "var(--border-soft)", background: "var(--panel-strong-bg)" }}
        >
          <div className="mb-5 flex items-end justify-between gap-4">
            <SectionHeading eyebrow="" title="Fantasy Graveyard" />
          </div>
          <TableShell>
            <LeagueTable stats={formerOwners} highlight={false} />
          </TableShell>
        </section>
      )}
    </div>
  );
}

function TrophyOverviewTwo({ theme, activeOwners, formerOwners, activeChampions, expelledMembers, mostPostseasonAppearances, totalSeasons, bestRecord, bestActiveWinRate }) {
  return (
    <div className="adaptive-stack">
      <section>
        <TrophyGalleryFrame>
          <div className="grid gap-6 xl:grid-cols-[1.2fr,0.8fr]">
            <div
              className="relative h-full rounded-[2.05rem] border p-2 shadow-2xl shadow-black/20"
              style={{ borderColor: "var(--border-strong)", background: "linear-gradient(135deg, rgba(255,255,255,0.02), rgba(0,0,0,0.12))" }}
            >
              <div
                className="relative flex h-full min-h-[320px] items-start justify-center overflow-hidden rounded-[1.75rem] border px-6 py-8 sm:px-8"
                style={{ borderColor: "var(--border-soft)", background: "#050505" }}
              >
                <div className="pointer-events-none absolute inset-0">
                  <img
                    src={footballHero}
                    alt=""
                    className="h-[calc(100%+0.5rem)] w-full translate-y-28 object-cover object-[center_top] sm:translate-y-[5.5rem]"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                </div>
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{ background: "linear-gradient(180deg, rgba(6, 6, 6, 0.94) 0%, rgba(6, 6, 6, 0.8) 22%, rgba(6, 6, 6, 0.35) 42%, rgba(6, 6, 6, 0.06) 60%, rgba(6, 6, 6, 0) 72%)" }}
                />
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{ boxShadow: "inset 0 0 0 1px rgba(242, 223, 180, 0.08)" }}
                />
                <div className="absolute inset-x-6 top-8 flex justify-center sm:inset-x-8 sm:top-10">
                  <TrophyHeroTitle />
                </div>
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-1">
              <ClassicPlaque title="Total Active Champions" value={activeChampions || "0"} />
              <ClassicPlaque title="Most Postseason Appearances" value={mostPostseasonAppearances.value} detail={mostPostseasonAppearances.name} />
            </div>
          </div>
        </TrophyGalleryFrame>
      </section>

      <section>
        <TrophyGalleryFrame subtle={true}>
          <div className="grid gap-5 md:grid-cols-3">
            <ClassicPlaque title="Total Seasons" value={totalSeasons || "--"} />
            <ClassicPlaque title="Best Season Record" value={bestRecord.value} detail={bestRecord.name} />
            <ClassicPlaque title="Best Active Win Rate" value={bestActiveWinRate.value} detail={bestActiveWinRate.name} />
          </div>
        </TrophyGalleryFrame>
      </section>

      <section
        className="adaptive-panel border shadow-2xl shadow-black/15"
        style={{ borderColor: "var(--border-soft)", background: "var(--panel-strong-bg)" }}
      >
        <div className="mb-5 flex items-end justify-between gap-4">
          <SectionHeading eyebrow="" title="Active Managers" />
        </div>
        <TableShell>
          <LeagueTable stats={activeOwners} highlight={true} />
        </TableShell>
      </section>

      {formerOwners.length > 0 && (
        <section
          className="adaptive-panel border shadow-2xl shadow-black/15"
          style={{ borderColor: "var(--border-soft)", background: "var(--panel-strong-bg)" }}
        >
          <div className="mb-5 flex items-end justify-between gap-4">
            <SectionHeading eyebrow="" title="Fantasy Graveyard" />
          </div>
          <TableShell>
            <LeagueTable stats={formerOwners} highlight={false} />
          </TableShell>
        </section>
      )}
    </div>
  );
}

export default function Home({ stats, rawData, theme, loading }) {
  const displayStats = useMemo(() => stats.map(normalizeSummaryRow), [stats]);
  const displayRawData = useMemo(() => (rawData || []).map(normalizeRawRow), [rawData]);
  const activeOwners = useMemo(() => displayStats.filter((row) => row.is_active), [displayStats]);
  const formerOwners = useMemo(() => displayStats.filter((row) => !row.is_active), [displayStats]);
  const activeChampions = useMemo(
    () => activeOwners.filter((row) => (row.championships ?? 0) > 0).length,
    [activeOwners]
  );
  const totalSeasons = useMemo(() => {
    const allYears = new Set(displayStats.flatMap((row) => (row.championship_years || []).map((year) => year)));
    return allYears.size || Math.max(...displayStats.map((row) => row.active_seasons ?? 0), 0);
  }, [displayStats]);
  const bestActiveWinRate = useMemo(() => {
    if (!activeOwners.length) return { name: "--", value: "--" };
    const best = activeOwners.reduce((leader, row) =>
      (row.regular_win_pct ?? 0) > (leader.regular_win_pct ?? 0) ? row : leader
    );
    return {
      name: best.name,
      value: `${Math.round((best.regular_win_pct ?? 0) * 100)}%`,
    };
  }, [activeOwners]);
  const mostPostseasonAppearances = useMemo(() => {
    if (!displayStats.length) return { name: "--", value: "--" };
    const best = displayStats.reduce((leader, row) =>
      (row.postseason_appearances ?? 0) > (leader.postseason_appearances ?? 0) ? row : leader
    );
    return {
      name: best.name,
      value: best.postseason_appearances ?? 0,
    };
  }, [displayStats]);
  const expelledMembers = useMemo(() => formerOwners.length, [formerOwners]);
  const bestRecord = useMemo(() => {
    if (!displayRawData?.length) return { name: "--", value: "--" };
    const best = displayRawData.reduce((leader, row) => {
      if (!leader) return row;

      const rowGames = (row.regular_wins ?? 0) + (row.regular_losses ?? 0);
      const leaderGames = (leader.regular_wins ?? 0) + (leader.regular_losses ?? 0);
      const rowPct = rowGames > 0 ? (row.regular_wins ?? 0) / rowGames : 0;
      const leaderPct = leaderGames > 0 ? (leader.regular_wins ?? 0) / leaderGames : 0;

      if (rowPct !== leaderPct) {
        return rowPct > leaderPct ? row : leader;
      }
      if ((row.regular_wins ?? 0) !== (leader.regular_wins ?? 0)) {
        return (row.regular_wins ?? 0) > (leader.regular_wins ?? 0) ? row : leader;
      }
      return (row.points_for ?? 0) > (leader.points_for ?? 0) ? row : leader;
    }, null);

    return {
      name: `${best.owner_name}${best.year ? ` ('${String(best.year).slice(-2)})` : ""}`,
      value: `${best.regular_wins ?? 0}-${best.regular_losses ?? 0}`,
    };
  }, [displayRawData]);

  const sharedProps = {
    theme,
    activeOwners,
    formerOwners,
    activeChampions,
    expelledMembers,
    mostPostseasonAppearances,
    totalSeasons,
    bestRecord,
    bestActiveWinRate,
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--accent-soft)] border-t-transparent" />
      </div>
    );
  }

  if (theme.id === "sportsbook") {
    return <SportsbookOverview {...sharedProps} />;
  }

  if (theme.id === "trophy") {
    return <TrophyOverview {...sharedProps} />;
  }

  if (theme.id === "trophy2") {
    return <TrophyOverviewTwo {...sharedProps} />;
  }

  return <ClassyOverview {...sharedProps} />;
}
